import { db } from "./firebaseService";
import { collection, doc, getDoc, getDocs, query, where, GeoPoint } from "firebase/firestore";
import { CampLocation } from "@/types/campLocation";
import { processReference } from "./locationVersion/referenceUtils";

// Function to fetch a single camp location by ID
export const getCampLocation = async (db: any, id: string): Promise<CampLocation | undefined> => {
  try {
    console.log("Fetching camp location with ID:", id);
    const docRef = doc(db, "campLocations", id);
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
      const locationData = {
        id: docSnap.id,
        ...docSnap.data(),
      } as Record<string, any>;
      
      console.log("Raw location data:", locationData);
      
      // Convert geopoint to string format if it exists
      if (locationData.address?.latLng instanceof GeoPoint) {
        locationData.address.latLng = `${locationData.address.latLng.latitude}, ${locationData.address.latLng.longitude}`;
      }
      
      // Process document references to ensure consistent format
      if (locationData.providedByOrg) {
        locationData.providedByOrg = processReference(locationData.providedByOrg);
      }
      
      if (locationData.lastEditedBy) {
        locationData.lastEditedBy = processReference(locationData.lastEditedBy);
      }
      
      // Process latestVersionId to ensure consistent format
      if (locationData.latestVersionId) {
        if (typeof locationData.latestVersionId === 'object' && locationData.latestVersionId.path) {
          // It's already a reference object
          locationData.latestVersionId = processReference(locationData.latestVersionId);
        } else if (typeof locationData.latestVersionId === 'string') {
          // It's a string ID, convert to reference format
          locationData.latestVersionId = {
            id: locationData.latestVersionId,
            path: `campLocationVersions/${locationData.latestVersionId}`
          };
        }
      }
      
      // Process publishedVersionId to ensure consistent format
      if (locationData.publishedVersionId) {
        if (typeof locationData.publishedVersionId === 'object' && locationData.publishedVersionId.path) {
          // It's already a reference object
          locationData.publishedVersionId = processReference(locationData.publishedVersionId);
        } else if (typeof locationData.publishedVersionId === 'string') {
          // It's a string ID, convert to reference format
          locationData.publishedVersionId = {
            id: locationData.publishedVersionId,
            path: `campLocationVersions/${locationData.publishedVersionId}`
          };
        }
      }
      
      // Type casting with fallback values
      const castedLocation = {
        ...locationData,
        name: locationData.name || '',
        visibility: locationData.visibility || 'v_draft',
        address: locationData.address || { country: 'USA' },
        doesWaitlist: locationData.doesWaitlist ?? null,
        doesScholarships: locationData.doesScholarships ?? null,
      } as CampLocation;
      
      console.log("Processed location data:", castedLocation);
      return castedLocation;
    } else {
      console.log("No such document!");
      return undefined;
    }
  } catch (error) {
    console.error("Error fetching document:", error);
    return undefined;
  }
};

// Function to fetch all camp locations
export const getAllCampLocations = async (db: any): Promise<CampLocation[]> => {
  try {
    const querySnapshot = await getDocs(collection(db, "campLocations"));
    const locations: CampLocation[] = [];
    querySnapshot.forEach((doc) => {
      const locationData = {
        id: doc.id,
        ...doc.data(),
      } as Record<string, any>;
      
      // Process document references to ensure consistent format
      if (locationData.providedByOrg) {
        locationData.providedByOrg = processReference(locationData.providedByOrg);
      }
      
      if (locationData.lastEditedBy) {
        locationData.lastEditedBy = processReference(locationData.lastEditedBy);
      }
      
      // Type casting with fallback values
      const castedLocation = {
        ...locationData,
        name: locationData.name || '',
        visibility: locationData.visibility || 'v_draft',
        address: locationData.address || { country: 'USA' },
        doesWaitlist: locationData.doesWaitlist ?? null,
        doesScholarships: locationData.doesScholarships ?? null,
      } as CampLocation;
      
      locations.push(castedLocation);
    });
    return locations;
  } catch (error) {
    console.error("Error fetching documents:", error);
    return [];
  }
};

// Function to fetch camp locations by visibility status
export const getCampLocationsByVisibility = async (db: any, visibility: string): Promise<CampLocation[]> => {
  try {
    const q = query(collection(db, "campLocations"), where("visibility", "==", visibility));
    const querySnapshot = await getDocs(q);
    const locations: CampLocation[] = [];
    
    querySnapshot.forEach((doc) => {
      const locationData = {
        id: doc.id,
        ...doc.data(),
      } as Record<string, any>;

      // Process document references to ensure consistent format
      if (locationData.providedByOrg) {
        locationData.providedByOrg = processReference(locationData.providedByOrg);
      }
      
      if (locationData.lastEditedBy) {
        locationData.lastEditedBy = processReference(locationData.lastEditedBy);
      }

      // Type casting with fallback values
      const castedLocation = {
        ...locationData,
        name: locationData.name || '',
        visibility: locationData.visibility || 'v_draft',
        address: locationData.address || { country: 'USA' },
        doesWaitlist: locationData.doesWaitlist ?? null,
        doesScholarships: locationData.doesScholarships ?? null,
      } as CampLocation;
      
      locations.push(castedLocation);
    });
    
    return locations;
  } catch (error) {
    console.error("Error fetching documents:", error);
    return [];
  }
};

// Function to fetch camp locations with optional organization filter
export const getCampLocations = async (db: any, organizationId?: string | null): Promise<CampLocation[]> => {
  try {
    let locations: CampLocation[] = [];
    
    if (organizationId) {
      // If org ID is provided, fetch locations for that org
      // When querying Firestore, we must use the actual DocumentReference
      const orgRef = doc(db, "campOrganizations", organizationId);
      const q = query(collection(db, "campLocations"), where("providedByOrg", "==", orgRef));
      const querySnapshot = await getDocs(q);
      
      querySnapshot.forEach((doc) => {
        const locationData = {
          id: doc.id,
          ...doc.data(),
        } as Record<string, any>;
  
        // Process document references to ensure consistent format
        if (locationData.providedByOrg) {
          locationData.providedByOrg = processReference(locationData.providedByOrg);
        }
        
        if (locationData.lastEditedBy) {
          locationData.lastEditedBy = processReference(locationData.lastEditedBy);
        }

        // Type casting with fallback values
        const castedLocation = {
          ...locationData,
          name: locationData.name || '',
          visibility: locationData.visibility || 'v_draft',
          address: locationData.address || { country: 'USA' },
          doesWaitlist: locationData.doesWaitlist ?? null,
          doesScholarships: locationData.doesScholarships ?? null,
        } as CampLocation;
        
        locations.push(castedLocation);
      });
    } else {
      // If no org ID, fetch all locations
      locations = await getAllCampLocations(db);
    }
    
    return locations;
  } catch (error) {
    console.error("Error fetching camp locations:", error);
    return [];
  }
};
